import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc 
} from 'firebase/firestore';
import { app } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const auth = getAuth(app);
  const db = getFirestore(app);
  const googleProvider = new GoogleAuthProvider();

  // Create user document with default role
  const createUserDocument = async (user, additionalData = {}) => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      const { email, displayName } = user;
      const createdAt = new Date();

      try {
        await setDoc(userRef, {
          displayName,
          email,
          role: 'customer', // Default role
          createdAt,
          failedAttempts: 0,
          isLocked: false,
          ...additionalData
        });
      } catch (error) {
        console.error('Error creating user document:', error);
      }
    }

    return userRef;
  };

  // Get user role from Firestore
  const getUserRole = useCallback(async (uid) => {
    if (!uid) return null;
    
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data().role || 'customer';
      }
      return 'customer';
    } catch (error) {
      console.error('Error getting user role:', error);
      return 'customer';
    }
  }, [db]);

  // Check if account is locked
  const checkAccountLock = async (email) => {
    try {
      // In a real implementation, you'd check against a users collection
      // For now, we'll use a simple in-memory check
      return isLocked;
    } catch (error) {
      console.error('Error checking account lock:', error);
      return false;
    }
  };

  // Update failed attempts
  const updateFailedAttempts = async (email, failed) => {
    try {
      if (failed) {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        
        if (newAttempts >= 5) {
          setIsLocked(true);
          // In a real implementation, you'd update this in Firestore
        }
      } else {
        setFailedAttempts(0);
        setIsLocked(false);
      }
    } catch (error) {
      console.error('Error updating failed attempts:', error);
    }
  };

  // Upgrade user role
  const upgradeUserRole = async (newRole) => {
    if (!currentUser) throw new Error('No user logged in');

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        role: newRole,
        roleUpgradedAt: new Date()
      });
      
      // Update local state immediately
      setUserRole(newRole);
      
      // Also refresh from Firestore to ensure consistency
      const updatedRole = await getUserRole(currentUser.uid);
      setUserRole(updatedRole);
      
              // Role upgraded successfully
      return true;
    } catch (error) {
      console.error('Error upgrading user role:', error);
      throw error;
    }
  };

  // Sign up function
  const signup = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      await updateProfile(result.user, { displayName });
      
      // Create user document with default role
      await createUserDocument(result.user, { displayName });
      
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Sign in function with account lock protection
  const signin = async (email, password) => {
    // Check if account is locked
    const locked = await checkAccountLock(email);
    if (locked) {
      throw new Error('Account is temporarily locked due to too many failed attempts. Please try again later.');
    }

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      // Reset failed attempts on successful login
      await updateFailedAttempts(email, false);
      return result;
    } catch (error) {
      // Increment failed attempts on failed login
      await updateFailedAttempts(email, true);
      throw error;
    }
  };

  // Google sign in
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      
      // Create user document if it doesn't exist
      await createUserDocument(result.user);
      
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  };

  // Sign out function
  const signout = () => {
    return signOut(auth);
  };

  // Check if user has required role
  const hasRole = (requiredRole) => {
    if (!userRole) return false;
    
    const roleHierarchy = {
      'customer': 1,
      'creator': 2,
      'admin': 3
    };
    
    return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
  };

  // Refresh user role from Firestore
  const refreshUserRole = async () => {
    if (!currentUser) return;
    
    try {
      const role = await getUserRole(currentUser.uid);
      setUserRole(role);
      console.log('🔄 User role refreshed:', role);
    } catch (error) {
      console.error('Error refreshing user role:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const role = await getUserRole(user.uid);
        setUserRole(role);
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth, getUserRole]);

  const value = {
    currentUser,
    userRole,
    signup,
    signin,
    signInWithGoogle,
    resetPassword,
    signout,
    upgradeUserRole,
    hasRole,
    refreshUserRole,
    loading,
    failedAttempts,
    isLocked
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 